import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { YouTubeFacade } from '../components/YouTubeFacade';
import { 
  Home, 
  Wrench, 
  Droplet, 
  Zap, 
  Shield, 
  Star,
  Phone,
  CheckCircle,
  ArrowRight,
  Play,
  Hammer,
  Users,
  Award,
  Settings,
  FileCheck
} from 'lucide-react';

const CustomHomePlumbing = () => {
  const navigate = useNavigate();
  
  const handleContactClick = () => {
    navigate('/#contact');
    // Small delay to ensure navigation happens first
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    // Meta tags are now handled by static generation in generate-static.cjs
    // This ensures consistency and avoids conflicts between React and static HTML

    // Add Custom Home Plumbing Service Schema
    const customHomeServiceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://colorado-plumbing.com/custom-home-plumbing#service',
      'name': 'Custom Home & Renovation Plumbing Services',
      'description': 'Expert plumbing services for custom homes, new construction, renovations, and ADU builds. Master plumber with 15 years construction experience specializing in luxury custom home projects.',
      'provider': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'email': 'codee@colorado-plumbing.com',
        'url': 'https://colorado-plumbing.com',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Highlands Ranch',
          'addressRegion': 'CO',
          'addressCountry': 'US'
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'reviewCount': '360',
          'bestRating': '5',
          'worstRating': '1'
        }
      },
      'areaServed': [
        {
          '@type': 'City',
          'name': 'Denver',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Highlands Ranch',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Littleton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Castle Rock',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Fraser',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Grand Lake',
          'addressRegion': 'CO'
        }
      ],
      'serviceType': 'Custom Home Plumbing',
      'category': 'Construction Plumbing Services',
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Custom Home & Renovation Plumbing Services',
        'itemListElement': [
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Underground Plumbing Installation',
              'description': 'Complete underground plumbing layout and installation for new construction'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Rough Plumbing Installation',
              'description': 'Rough-in plumbing for custom homes, additions, and renovations'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Finish Plumbing Installation',
              'description': 'Luxury fixture installation and finish plumbing work'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Gas Piping Installation',
              'description': 'Domestic water, drainage, and gas piping systems'
            }
          }
        ]
      },
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'USD',
        'availability': 'https://schema.org/InStock',
        'description': 'Professional custom home plumbing services with transparent pricing and master plumber expertise'
      },
      'additionalProperty': [
        {
          '@type': 'PropertyValue',
          'name': 'Construction Experience',
          'value': '15 Years'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Master Plumber Owned',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Custom Home Specialist',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Permit Coordination',
          'value': 'Yes'
        }
      ]
    };

    // Inject the service schema
    let serviceSchemaScript = document.querySelector('#custom-home-service-schema');
    if (serviceSchemaScript) {
      serviceSchemaScript.textContent = JSON.stringify(customHomeServiceSchema);
    } else {
      serviceSchemaScript = document.createElement('script');
      serviceSchemaScript.setAttribute('id', 'custom-home-service-schema');
      serviceSchemaScript.setAttribute('type', 'application/ld+json');
      serviceSchemaScript.textContent = JSON.stringify(customHomeServiceSchema);
      document.head.appendChild(serviceSchemaScript);
    }
  }, []);

  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    loadBirdEyeWidget();

    // Cleanup on unmount
    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-neutral-light">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-deep to-authority-navy">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10 py-16 md:py-24">
            <div className="inline-block px-4 py-2 mb-6 rounded-full bg-trust-light text-trust-deep font-semibold text-sm border border-trust-primary/20">
              <Home className="inline-block mr-2" size={20} />
              Colorado's Custom Home Plumbing Specialists
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-neutral-white">
              Custom Home & Renovation Plumbing Colorado
            </h1>
            <p className="text-xl mb-8 text-trust-light/90">
              Master plumber with 15 years construction experience specializing in custom homes, new construction, 
              renovations, and ADU builds. From <Link to="/sewer-repair-replacement" className="text-action-warm hover:text-white underline">underground plumbing</Link> to 
              luxury finish work with <Link to="/pricing" className="text-action-warm hover:text-white underline">transparent pricing</Link>.
            </p>
            
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
              <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                <p>Reviews loading... See our 500+ verified reviews on Google and BirdEye.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <button 
                data-token="d42111216a654eb1a935ffcd97b30355" 
                data-orgname="Colorado-First-Plumbing-and-Sewer-LLC" 
                className="hcp-button bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                onClick={() => {
                  if (window.HCPWidget) {
                    window.HCPWidget.openModal();
                  }
                }}
              >
                Get Free Estimate
              </button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 md:py-20 bg-neutral-light">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-authority-navy">Custom Home & Renovation Plumbing Services</h2>
              <p className="text-lg text-neutral-gray">
                From underground installations to luxury finish work, our master plumber team handles every aspect of custom home and 
                <Link to="/bathroom-kitchen-remodel" className="text-trust-primary underline hover:text-action-primary"> renovation plumbing</Link> projects 
                with professional expertise and coordination.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Underground Plumbing Installation */}
              <div className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative mb-4 rounded-lg overflow-hidden">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/custom-home-underground-layout.jpg" 
                    alt="Underground Plumbing Installation Layout"
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Underground Systems
                    </span>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 text-authority-navy">Underground Plumbing Installation</h3>
                <p className="text-neutral-gray mb-4">Complete underground plumbing layout and installation for new construction and major renovations.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Foundation Plumbing Layout</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Sewer Line Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Water Service Connections</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Cleanout Accessibility Planning</span>
                  </li>
                </ul>
              </div>

              {/* Rough Plumbing Installation */}
              <div className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative mb-4 rounded-lg overflow-hidden">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/Custom-Home-Denver-Front-view.jpg" 
                    alt="Custom Home Denver Rough Plumbing"
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Rough-In Plumbing
                    </span>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 text-authority-navy">Rough Plumbing Installation & Layout</h3>
                <p className="text-neutral-gray mb-4">Professional rough-in plumbing for custom homes, additions, and major renovation projects.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Whole-House Rough-In Plumbing</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Multi-Bathroom Installations</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Kitchen & Utility Rough-In</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Code Compliance & Testing</span>
                  </li>
                </ul>
              </div>

              {/* Domestic Water, Drainage & Gas Piping */}
              <div className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-3 rounded-lg bg-gradient-to-r from-red-500 to-red-600 w-fit mb-4 shadow-md">
                  <Droplet className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-authority-navy">Domestic Water, Drainage & Gas Piping</h3>
                <p className="text-neutral-gray mb-4">Complete piping systems installation for water supply, drainage, and gas lines in custom homes.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Domestic Water Distribution</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Drainage & Vent Systems</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Gas Piping Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">System Pressure Testing</span>
                  </li>
                </ul>
              </div>

              {/* Finish Plumbing Installation */}
              <div className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative mb-4 rounded-lg overflow-hidden">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/installing-toilet.JPEG" 
                    alt="Finish Plumbing Installation"
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Finish Work
                    </span>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 text-authority-navy">Finish Plumbing Installation of Fixtures</h3>
                <p className="text-neutral-gray mb-4">Professional installation of fixtures, valves, and finish plumbing components for custom homes.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Luxury Fixture Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Under-Sink Drainage & Valves</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Faucet & Shut-Off Valve Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Final System Testing</span>
                  </li>
                </ul>
              </div>

              {/* Specialized Installations */}
              <div className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-3 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 w-fit mb-4 shadow-md">
                  <Zap className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-authority-navy">Specialized Installations</h3>
                <p className="text-neutral-gray mb-4">Expert installation of Roman style tubs, shower pans, water heaters, and custom plumbing features.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Roman Style Tubs & Luxury Bathing</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Custom Shower Pan Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">
                      <Link to="/water-heater-pricing" className="text-trust-primary hover:text-action-primary">Water Heater Systems</Link>
                    </span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Steam Rooms & Spa Features</span>
                  </li>
                </ul>
              </div>

              {/* Project Types & Permits */}
              <div className="group bg-neutral-white rounded-xl p-6 shadow-lg border border-neutral-border hover:shadow-2xl hover:border-trust-primary transition-all duration-300 transform hover:-translate-y-2">
                <div className="p-3 rounded-lg bg-gradient-to-r from-green-500 to-green-600 w-fit mb-4 shadow-md">
                  <FileCheck className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3 text-authority-navy">Project Types & Permits</h3>
                <p className="text-neutral-gray mb-4">Experience in new house builds, additions, ADU conversions, and all permit coordination.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">New House Builds & Construction</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Home Additions & Extensions</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">ADU Garage to Living Space Builds</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-success-primary mr-2" size={16} />
                    <span className="text-sm text-authority-dark">Permit Coordination & Inspections</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="py-20 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
          <div className="container-custom">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Choose Colorado First for Custom Home Projects?</h2>
              <p className="text-lg text-trust-light/90">
                Our master plumber owned company brings 15 years of specialized construction experience that contractors and homeowners trust. 
                We understand custom home complexities and coordinate seamlessly with other trades to deliver professional results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 15 Years Construction Experience */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-gradient-to-r from-success-primary to-success-eco w-fit mb-4 shadow-md">
                  <Award className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">15 Years Construction Experience</h3>
                <p className="text-trust-light/90 mb-4">
                  Master Plumber <Link to="/team/codee-jessen" className="text-action-warm underline hover:text-white">Codee Jessen</Link> brings 15 years of specialized construction plumbing experience, 
                  including multifamily residential, custom home building, and major renovation projects across Colorado. Our experience includes everything from 
                  mountain homes like our Fraser Grand Lake project to luxury Denver custom builds.
                </p>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="font-semibold">✓ Master plumber owned & operated</p>
                  <p className="font-semibold">✓ 15 years construction plumbing experience</p>
                  <p className="font-semibold">✓ Custom home & luxury project specialist</p>
                  <p className="font-semibold">✓ Multifamily residential expertise</p>
                </div>
              </div>

              {/* Transparent Pricing & Professional Coordination */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="p-3 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 w-fit mb-4 shadow-md">
                  <Shield className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Transparent Pricing & Professional Coordination</h3>
                <p className="text-trust-light/90 mb-4">
                  We're the ONLY contractor in Colorado showing actual <Link to="/pricing" className="text-action-warm underline hover:text-white">plumbing pricing online</Link>. 
                  No surprises, no hidden fees, just honest pricing you can trust. Our extensive construction experience includes professional coordination 
                  with general contractors, architects, and designers on complex custom projects.
                </p>
                <div className="bg-white/10 rounded-lg p-4">
                  <p className="font-semibold">✓ Complete transparent pricing online</p>
                  <p className="font-semibold">✓ Professional contractor coordination</p>
                  <p className="font-semibold">✓ Architect & designer collaboration</p>
                  <p className="font-semibold">✓ No hidden fees guaranteed</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Recent Projects */}
        <section className="py-20 bg-neutral-light">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-authority-navy">Our Recent Custom Home & Renovation Projects</h2>
              <p className="text-lg text-neutral-gray">
                See examples of our expert custom home and renovation plumbing projects with full documentation across Colorado.
              </p>
            </div>

            {/* Fraser Grand Lake Project */}
            <div className="mb-16">
              <div className="max-w-6xl mx-auto">
                <h3 className="text-2xl font-bold text-center mb-8 text-authority-navy">
                  Fraser Grand Lake Custom Home Renovation Project
                </h3>
                <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                  <div className="order-2 md:order-1">
                    <div className="relative">
                      <YouTubeFacade
                        videoId="Haua5tuy7PM"
                        title="Project Start Grand Lake - Fraser Renovation - Plumbing Install"
                        autoplay={true}
                        muted={true}
                        controls={true}
                        className="w-full max-w-md mx-auto rounded-lg shadow-lg"
                      />
                      <div className="absolute bottom-2 left-2">
                        <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold opacity-90">
                          🎥 Click to Play Video
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="order-1 md:order-2">
                    <h4 className="text-xl font-semibold mb-4 text-primary">
                      Fraser Grand Lake Project Start - Plumbing Installation
                    </h4>
                    <p className="text-gray-700 mb-6">
                      Master Plumber Codee Jessen documents the beginning phases of a major renovation project in Fraser, Colorado. 
                      This video shows the project start for Colorado First Plumbing's complete renovation of the drainage and water 
                      piping systems in this mountain home, including planning for multiple new bathroom additions.
                    </p>
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h5 className="font-semibold mb-3 text-primary">Project Highlights:</h5>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Complete drainage system replacement</li>
                        <li>• New water line installation throughout</li>
                        <li>• Multiple bathroom additions</li>
                        <li>• Mountain home specialized solutions</li>
                        <li>• Code compliance & permit coordination</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Grand Lake Testing Video */}
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-primary">
                      Testing Newly Installed Rough Plumbing
                    </h4>
                    <p className="text-gray-700 mb-6">
                      Professional testing of the newly installed rough plumbing at the Grand Lake renovation project. 
                      This comprehensive water test ensures all drainage systems function properly and pass inspection 
                      before moving to the next phase of construction.
                    </p>
                    <div className="bg-green-50 p-6 rounded-lg">
                      <h5 className="font-semibold mb-3 text-primary">Testing & Inspection Process:</h5>
                      <ul className="space-y-2 text-gray-700">
                        <li>• Comprehensive water drainage testing</li>
                        <li>• Leak detection and verification</li>
                        <li>• Code compliance confirmation</li>
                        <li>• Rough plumbing inspection passage</li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <YouTubeFacade
                      videoId="Bir0jiYM4n4"
                      title="Testing PVC drainage with water to find leaks and pass Inspection. Grand Lake Colorado."
                      autoplay={true}
                      muted={true}
                      className="w-full rounded-lg shadow-lg"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Projects Gallery */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Highlands Ranch Basement Project */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/Highlands Ranch Basement Project_20250621_122448_0000.png" 
                    alt="Highlands Ranch Basement Finishing Project"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Highlands Ranch
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-authority-navy">Highlands Ranch Basement Finishing</h3>
                  <p className="text-neutral-gray text-sm">Complete basement finishing plumbing including bathroom addition, wet bar, and utility connections</p>
                </div>
              </div>

              {/* Lone Tree Basement Project */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/lone-tree-bathroom-basement-install-rough-plumbing.jpg" 
                    alt="Lone Tree Basement Bathroom Installation"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Lone Tree
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-authority-navy">Lone Tree Basement Bathroom Install</h3>
                  <p className="text-neutral-gray text-sm">Professional basement bathroom rough plumbing installation in Lone Tree custom home project</p>
                </div>
              </div>

              {/* Underground Aurora Project */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/custom-home-underground-aurora.jpg" 
                    alt="Custom Home Underground Plumbing Aurora"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Aurora Custom
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-authority-navy">Aurora Custom Home Underground</h3>
                  <p className="text-neutral-gray text-sm">Underground plumbing installation for Aurora custom home with comprehensive layout planning</p>
                </div>
              </div>

              {/* Bar Sink Rough Plumbing */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/bar-sink-basement-rough-plumbing.jpg" 
                    alt="Basement Bar Sink Rough Plumbing"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Basement Bar
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-authority-navy">Basement Bar Sink Installation</h3>
                  <p className="text-neutral-gray text-sm">Professional rough plumbing for basement wet bar with proper drainage and water supply</p>
                </div>
              </div>

              {/* Gas Pipe Installation */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/gas-pipe-rough-plumbing-arvada.jpg" 
                    alt="Gas Pipe Rough Plumbing Arvada"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Gas Installation
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-authority-navy">Gas Pipe Rough Plumbing</h3>
                  <p className="text-neutral-gray text-sm">Professional gas pipe installation for Arvada custom home with kitchen appliance connections</p>
                </div>
              </div>

              {/* Ejector Pump Installation */}
              <div className="group bg-neutral-white rounded-xl shadow-lg overflow-hidden border border-neutral-border hover:shadow-2xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/golden-sewer-ejector-pump-pit-install.JPEG" 
                    alt="Golden Sewer Ejector Pump Installation"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-indigo-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Ejector System
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-authority-navy">Sewer Ejector Pump Installation</h3>
                  <p className="text-neutral-gray text-sm">Expert sewer ejector pump system installation in Golden for below-grade plumbing</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Reviews Section */}
        <section className="py-20 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="flex items-center justify-center mb-4">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={24} />
                  ))}
                </div>
                <span className="ml-3 text-2xl font-bold">4.8/5 (500+ Reviews)</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Custom Home Clients Love Our Expert Service</h2>
              <p className="text-lg text-trust-light/90">
                See what homeowners and contractors have to say about our custom home and renovation plumbing services across Colorado.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Review 1 - Construction Experience */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-action-warm">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm font-medium bg-white/20 px-2 py-1 rounded">Google</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "The owner is a certified master plumber that truly cares about his customers. They are quick to respond and are the best plumbers I have ever seen. They earned my full trust."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Timothy B.</p>
                  <p className="text-trust-light/80">Custom Home Client</p>
                </div>
              </div>

              {/* Review 2 - Renovation Project */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-action-warm">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm font-medium bg-white/20 px-2 py-1 rounded">BirdEye</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "Codee is awesome and an experienced plumber. He went above & beyond repairing a leak and installing a new shower pan & bathroom fixtures. He is our 'go-to' guy for any plumbing needs."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Garry C.</p>
                  <p className="text-trust-light/80">Renovation Client</p>
                </div>
              </div>

              {/* Review 3 - Professional Coordination */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border-l-4 border-action-warm">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1 text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} className="fill-current" />
                    ))}
                  </div>
                  <span className="ml-2 text-sm font-medium bg-white/20 px-2 py-1 rounded">Google</span>
                </div>
                <p className="text-trust-light/90 mb-4 italic">
                  "Best plumbing company I've ever used. Always responds promptly with great service fairly priced. It's what a property investor needs to operate effectively."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Charles J.</p>
                  <p className="text-trust-light/80">Property Investor</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link 
                to="/reviews"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-action-primary to-action-urgent text-white rounded-lg font-semibold hover:shadow-xl transition-all"
              >
                View All 500+ Reviews
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-br from-trust-deep to-authority-navy text-white">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready for Professional Custom Home Plumbing?</h2>
            <p className="text-xl mb-8 text-trust-light/90">
              From underground installations to luxury finish work, we're Colorado's trusted custom home plumbing specialists. 
              Get started with our <Link to="/pricing" className="text-action-warm underline hover:text-white">transparent pricing</Link> and master plumber expertise.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              
              <button 
                onClick={handleContactClick}
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                Schedule Free Consultation
              </button>
            </div>
            
            <div className="text-center">
              <p className="text-trust-light/80 text-sm">
                ✓ Master Plumber Owned  ✓ 15 Years Construction Experience  ✓ Custom Home Specialists  ✓ <Link to="/pricing" className="text-action-warm underline hover:text-white">Transparent Pricing</Link>
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default CustomHomePlumbing;